import { Component, inject, OnInit, OnDestroy, AfterViewInit, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MenuController, Platform, NavController } from '@ionic/angular/standalone';
import { RouterModule } from '@angular/router';
import { Subscription, filter, take } from 'rxjs';
import { IonApp, IonSplitPane, IonMenu, IonHeader, IonToolbar, IonTitle,
         IonContent, IonList, IonItem, IonIcon, IonLabel, IonRouterOutlet, IonMenuToggle, IonImg,
         IonTabs, IonTabBar, IonTabButton } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { briefcaseOutline, documentTextOutline, cubeOutline, personOutline, menuOutline, addOutline,
         homeOutline, searchOutline, swapHorizontalOutline } from 'ionicons/icons';
import { ClerkService } from 'ngx-clerk';
import { environment } from 'src/environments/environment';
import { UserService } from './services/user.service';
import { ThemeService } from './services/theme.service';
import { HeaderService } from './services/header.service';
import { AppHeaderComponent } from './components/app-header/app-header.component';
import { LegalConsentPayload, LEGAL_CONSENT_STORAGE_KEY, LegalService } from './services/legal.service';

// Add the icons we're using
addIcons({
  'briefcase': briefcaseOutline,
  'document-text': documentTextOutline,
  'cube': cubeOutline,
  'person': personOutline,
  'menu-outline': menuOutline,
  'add': addOutline,
  'home-outline': homeOutline,
  'search-outline': searchOutline,
  'swap-horizontal-outline': swapHorizontalOutline,
  'briefcase-outline': briefcaseOutline,
  'person-outline': personOutline,
});

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [
    RouterModule,
    IonApp,
    IonSplitPane,
    IonMenu,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonList,
    IonItem,
    IonIcon,
    IonLabel,
    IonRouterOutlet,
    IonMenuToggle,
    IonImg,
    IonTabs,
    IonTabBar,
    IonTabButton,
    AppHeaderComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  // Common pages for all users (always visible)
  commonPages = [
    { title: 'Home', url: '/home', icon: 'home-outline' },
    { title: 'Explore', url: '/explore', icon: 'search-outline' },
    { title: 'Trades', url: '/trades', icon: 'swap-horizontal-outline' },
    { title: 'Business', url: '/business', icon: 'briefcase-outline' },
    { title: 'Account', url: '/account', icon: 'person-outline' },
  ];

  isAuthRoute = false;
  isAuthenticated = false;
  isMobile = false;

  // References to logo elements for cleanup
  private darkLogoElement: HTMLImageElement | null = null;
  private lightLogoElement: HTMLImageElement | null = null;

  // References for cleanup of observers and timeouts
  private logoObserver: MutationObserver | null = null;
  private logoObserverTimeout: number | null = null;

  private routerSubscription!: Subscription;
  private authSubscription!: Subscription;
  private themeSubscription!: Subscription;

  private router = inject(Router);
  private menu = inject(MenuController);
  private navCtrl = inject(NavController);
  private _clerk = inject(ClerkService);
  private userService = inject(UserService);
  private themeService = inject(ThemeService);
  private headerService = inject(HeaderService);
  private platform = inject(Platform);
  private legalService = inject(LegalService);

  constructor() {
    this._clerk.__init({
      publishableKey: environment.clerkPublishableKey,
    });

    this.checkMobile();

    // Initialize isAuthRoute based on the current URL
    this.isAuthRoute = this.router.url.includes('/auth');

    // Track authentication state using observables
    this.authSubscription = this._clerk.user$.subscribe(clerkUser => {
      const wasAuthenticated = this.isAuthenticated;
      this.isAuthenticated = !!clerkUser;

      // If user is authenticated, fetch the profile before leaving the auth route
      if (this.isAuthenticated && clerkUser) {
        // Fetch/Refresh full profile from backend
        this.userService.getCurrentUser().pipe(take(1)).subscribe(user => {
          if (user) {
            console.info('[AppComponent] Successfully fetched user profile:', user.email);

            // Handle redirection only if we just logged in AND we are on an auth route
            if (!wasAuthenticated && this.isAuthRoute) {
              this.syncPendingConsent();
            }
          } else {
            console.warn('[AppComponent] No user profile returned from API');

            if (!wasAuthenticated && this.isAuthRoute) {
              // Fallback if no user profile but we are on auth route
              this.router.navigate(['/home']);
            }
          }
        });
      }
    });

    console.info('[AppComponent] Build:', environment.buildNumber, 'Production:', environment.production, 'API:', environment.apiUrl);
  }

  private syncPendingConsent(): void {
    const storedConsent = localStorage.getItem(LEGAL_CONSENT_STORAGE_KEY);

    if (!storedConsent) {
      this.router.navigate(['/home']);
      return;
    }

    let consent: LegalConsentPayload;

    try {
      consent = JSON.parse(storedConsent);
    } catch {
      localStorage.removeItem(LEGAL_CONSENT_STORAGE_KEY);
      this.router.navigate(['/home']);
      return;
    }

    this.legalService.acceptTerms(consent).subscribe({
      next: () => {
        this.userService.refreshUser().subscribe(() => {
          localStorage.removeItem(LEGAL_CONSENT_STORAGE_KEY);
          this.router.navigate(['/home']);
        });
      },
      error: () => {
        this.router.navigate(['/home']);
      },
    });
  }

  ngOnInit() {
    // Subscribe to router events to track auth routes
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      // Check if the current URL includes '/auth'
      this.isAuthRoute = event.url.includes('/auth');

      // Reset back button when on main tab roots
      const mainTabs = ['/home', '/explore', '/trades', '/business', '/account'];
      if (mainTabs.includes(event.url)) {
        this.headerService.setShowBackButton(false);
      }
    });

    // Subscribe to theme changes to ensure proper logo display
    this.themeSubscription = this.themeService.darkMode$.subscribe(isDark => {
      // Update logo visibility based on current theme
      this.updateLogoVisibility(isDark);
    });
  }

  ngAfterViewInit() {
    // Get the current theme state
    // Note: We need to access the current value of the BehaviorSubject
    // This is a workaround since we can't directly access the private property
    let currentTheme = false;
    this.themeService.darkMode$.subscribe(isDark => {
      currentTheme = isDark;
    }).unsubscribe(); // Immediately unsubscribe to avoid memory leaks

    // Update logo visibility based on initial theme
    this.updateLogoVisibility(currentTheme);
  }

  ngOnDestroy() {
    // Unsubscribe from all subscriptions
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }

    // Clean up event handlers on logo elements
    if (this.darkLogoElement) {
      this.darkLogoElement.onload = null;
      this.darkLogoElement.onerror = null;
      this.darkLogoElement = null;
    }

    if (this.lightLogoElement) {
      this.lightLogoElement.onload = null;
      this.lightLogoElement.onerror = null;
      this.lightLogoElement = null;
    }

    // Clean up any active logo observer and timeout
    this.cleanupLogoObserver();
  }

  /**
   * Updates logo visibility based on the current theme
   * @param isDark Whether dark mode is active
   */
  private updateLogoVisibility(isDark: boolean) {
    // Skip logo update only if we're on an auth route (auth page handles its own logo)
    // but allow updates for navbar logo regardless of authentication state
    if (this.isAuthRoute) {
      return;
    }

    // If no logo elements are present (we now use text), exit early to avoid observers/timeouts
    const hasDark = !!document.querySelector('.logo-dark');
    const hasLight = !!document.querySelector('.logo-light');
    if (!hasDark && !hasLight) {
      return;
    }

    // Use MutationObserver to detect when logo elements are available in the DOM
    this.waitForLogoElements().then(([darkLogo, lightLogo]) => {
      // Double-check body classes to ensure we're using the correct theme state
      const bodyClasses = document.body.classList;

      // If body has explicit theme classes, use those instead of the parameter
      let effectiveTheme = isDark;
      if (bodyClasses.contains('dark')) {
        effectiveTheme = true;
      } else if (bodyClasses.contains('light')) {
        effectiveTheme = false;
      }

      // Force reload images by updating src with current timestamp
      const timestamp = new Date().getTime();
      const darkSvgPath = 'assets/images/ccn-logo-dark.svg';
      const lightSvgPath = 'assets/images/ccn-logo-light.svg';

      // Set SVG paths with timestamp
      const darkSrc = darkSvgPath + '?v=' + timestamp;
      const lightSrc = lightSvgPath + '?v=' + timestamp;

      // Set visibility based on effective theme
      if (effectiveTheme) {
        darkLogo.style.display = 'block';
        lightLogo.style.display = 'none';
      } else {
        darkLogo.style.display = 'none';
        lightLogo.style.display = 'block';
      }

      // Store references to logo elements for cleanup
      this.darkLogoElement = darkLogo;
      this.lightLogoElement = lightLogo;

      // Add load event listeners to verify images are loading
      if (this.darkLogoElement) {
        this.darkLogoElement.onerror = () => console.error('Failed to load dark SVG logo');
      }

      if (this.lightLogoElement) {
        this.lightLogoElement.onerror = () => console.error('Failed to load light SVG logo');
      }

      // Set new src to force reload
      darkLogo.src = darkSrc;
      lightLogo.src = lightSrc;
    }).catch(error => {
      console.error('Failed to find logo elements:', error);
    });
  }

  /**
   * Waits for logo elements to be available in the DOM
   * @returns Promise that resolves with the logo elements when they're available
   */
  private waitForLogoElements(): Promise<[HTMLImageElement, HTMLImageElement]> {
    return new Promise((resolve, reject) => {
      // Clean up any existing observer and timeout
      this.cleanupLogoObserver();

      // First, check if elements already exist
      let darkLogo = document.querySelector('.logo-dark') as HTMLImageElement;
      let lightLogo = document.querySelector('.logo-light') as HTMLImageElement;

      if (darkLogo && lightLogo) {
        resolve([darkLogo, lightLogo]);
        return;
      }

      // Set up a MutationObserver to watch for changes to the DOM
      this.logoObserver = new MutationObserver(() => {
        darkLogo = document.querySelector('.logo-dark') as HTMLImageElement;
        lightLogo = document.querySelector('.logo-light') as HTMLImageElement;

        if (darkLogo && lightLogo) {
          this.cleanupLogoObserver(); // Clean up observer and timeout
          resolve([darkLogo, lightLogo]);
        }
      });

      // Start observing with a configuration to watch for child list changes
      this.logoObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Set a timeout to avoid waiting indefinitely
      this.logoObserverTimeout = window.setTimeout(() => {
        this.cleanupLogoObserver();
        reject(new Error('Timed out waiting for logo elements'));
      }, 5000); // 5 second timeout
    });
  }

  /**
   * Cleans up any active logo observer and timeout
   */
  private cleanupLogoObserver(): void {
    // Disconnect observer if it exists
    if (this.logoObserver) {
      this.logoObserver.disconnect();
      this.logoObserver = null;
    }

    // Clear timeout if it exists
    if (this.logoObserverTimeout !== null) {
      window.clearTimeout(this.logoObserverTimeout);
      this.logoObserverTimeout = null;
    }
  }

  navigateTo(url: string) {
    this.router.navigateByUrl(url);
  }

  resetTabToRoot(root: string) {
    // Send the section back to its root, clearing any detail page in that tab's stack.
    this.navCtrl.navigateRoot(root);
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkMobile();
  }

  private checkMobile() {
    this.isMobile = this.platform.width() < 768;
  }
}
